import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useQueryClient } from "react-query";
import DeleteIcon from "@mui/icons-material/Delete";

import { useSnackBar } from "src/context/SnackBarContext";

import AlertDialog from "src/components/AlertDialog";
import useFetchData from "../utilities/useFetchData";
import PageContainer from "src/components/container/PageContainer";
import DashboardCard from "src/components/shared/DashboardCard";
import DataTable from "src/components/DataTable";
// import AddCategory from "./components/AddCategory";
import { deleteData } from "../utilities/api";

import { redirectUrl } from "src/config";
import Loader from "src/components/Loader";
import Loadable from "src/layouts/full/shared/loadable/Loadable";
import AddPlanMaster from "../master/components/AddPlanMaster";

const columns = [
  { field: "id", headerName: "ID", width: 90 },
  {
    field: "plan_name",
    headerName: "Plan Name",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "price",
    headerName: "Rate",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "offer_price",
    headerName: "Offer",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "days",
    headerName: "duration",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "qty",
    headerName: "qty",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "status",
    headerName: "status",
    renderCell:(params)=><Stack direction="row" gap={2} color={params.status==="Active"?"green":"red"}>{params.status}</Stack>,
    flex: 1,
    minWidth: 150,
  },
];

function PlanMaster() {
  const { showSnackbar } = useSnackBar();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [editData, setEditData] = useState({});
  const { data, isLoading, error } = useFetchData(
    "plans",
    "/api/showUsersPlanMaster"
  );
  
  const [alert, setAlert] = useState({
    open: false,
    onConfirm: null,
    onCancel: null,
    title: "",
    message: "",
    buttonType: "",
    buttonText: "",
  });

  const handleClickOpen = (data) => {
    if (data.plan_name) {
      setEditData(data);
    } else {
      setEditData({});
    }

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (isLoading) return <Loadable />;
  if (error)
    return (
      <>
        <Loadable />
        <p>Error: {error.message}</p>
      </>
    );

  const handleRefetch = () => {
    queryClient.invalidateQueries("categoryData");
  };

  const handleDelete = async (params) => {
    setLoading(true);
    try {
      const response = await deleteData(
        `/api/deleteUsersPlanMaster?id=${params.id}`
      );
      if (response.success) {
        showSnackbar("error", response.message);
        handleRefetch();
        cancelPrint();
      } else {
        showSnackbar("warning", response.message);
      }
    } catch (error) {
      if (error.response.status === 401 || error.response.status === 403) {
        window.location.href = redirectUrl;
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelPrint = () => {
    setAlert({
      open: false,
    });
  };

  const renderButtons = (params) => (
    <Stack direction="row" gap={2}>
      <Button
        size="small"
        variant="contained"
        color="secondary"
        onClick={() => handleClickOpen(params)}
      >
        Edit
      </Button>
      <IconButton
        aria-label="delete"
        color="error"
       size="small"
        onClick={() =>
          openAlert({
            open: true,
            onConfirm: () => handleDelete(params),
            onCancel: cancelPrint,
            title: "Delete",
            message: "Do you want to delete this product?",
            buttonType: "error",
            buttonText: "Delete",
            row: params,
          })
        }
      >
        <DeleteIcon />
      </IconButton>
    </Stack>
  );

  const columnsWithButtons = [
    ...columns,
    {
      field: "actions",
      headerName: "Actions",
      renderCell: renderButtons,
      sortable: false,
      filterable: false,
      flex: 1,
      minWidth: 150,
    },
  ];

  const openAlert = ({
    open,
    onConfirm,
    onCancel,
    title,
    message,
    buttonType,
    buttonText,
    row,
  }) => {
    setAlert({
      open,
      onConfirm,
      onCancel,
      title,
      message,
      buttonType,
      buttonText,
    });
  };

  return (
    <>
      <Loader start={loading} />
      <AlertDialog
        open={alert.open}
        onConfirm={alert.onConfirm}
        onCancel={alert.onCancel}
        title={alert.title}
        message={alert.message}
        buttonType={alert.buttonType}
        buttonText={alert.buttonText}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        labelled="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <AddPlanMaster
            onClose={handleClose}
            refetch={handleRefetch}
            editData={editData}
          />
        </DialogContent>
      </Dialog>
      <PageContainer>
        <DashboardCard title="Plan Master">
          <Box textAlign="right">
            <Button
              sx={{ mb: 1.5, width: 180 }}
              onClick={() => handleClickOpen({})}
              variant="contained"
              color="primary"
            >
              Create
            </Button>
          </Box>
          <Box>
            <DataTable
              columns={columnsWithButtons}
              rows={data.plans.data}
            />
          </Box>
        </DashboardCard>
      </PageContainer>
    </>
  );
}

export default PlanMaster;
